import { forwardRef, useEffect, useRef, useState } from 'react';
import { Box, Button, Grid, MenuItem, Select, SelectChangeEvent } from '@mui/material';

import { useAppDispatch } from '../../store/hooks';
import { fetchLeadSources, fetchLeadSubSources, fetchUnofyCrmAgents } from '../../store/accountThunk';

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  transition: '0ms',
  width: 650,
  borderRadius: 2,
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
  padding: 0
};

// Same borderless dropdown look the Registration & Legal form uses.
const selectSx = {
  width: '100%',
  boxShadow: 'none',
  paddingTop: '0px !important',
  borderRadius: '7px !important',
  marginTop: '4px',
  '& .MuiOutlinedInput-input': { p: 0.7 },
  marginBottom: '3px',
  '.MuiOutlinedInput-notchedOutline': { border: 0 },
  '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { border: 0 },
  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
};

const menuProps = {
  PaperProps: {
    sx: { border: '1.5px solid #e9ecf1', boxShadow: 'none', borderRadius: '5px', maxHeight: 320 }
  }
};

const labelStyle = { fontWeight: 'normal' as 'normal', color: '#9da7b7', fontSize: 14 };

export interface CrmLeadValue {
  sourceRef?: string;
  sourceLabel?: string;
  subSourceRef?: string;
  subSourceLabel?: string;
  /** What the invoice stores as its `source`: the sub-source, or the source when it has none. */
  sourceValue?: string;
  agentId?: string;
  agentName?: string;
}

/** The attribution the imported CRM lead already carries, as free-text labels. */
export interface CrmLeadDefaults {
  leadId?: string;
  sourceName?: string;
  subSourceName?: string;
  agentName?: string;
}

interface DialogLeadFormProps {
  open: boolean;
  value?: CrmLeadValue;
  defaults?: CrmLeadDefaults;
  onSave: (value: CrmLeadValue) => void;
  onClose: () => void;
}

/**
 * Lead attribution for an invoice: which source/sub-source brought the buyer in
 * and which CRM agent handled them. Source and sub-source come from our own
 * account-scoped CRM masters; agents come from the Unofy CRM user list.
 *
 * The invoice only keeps the sub-source label (as its `source`); the agent rides
 * along on the save request and is forwarded to the CRM's patient-visit call.
 */
// forwardRef so MUI's Modal can hand the ref to the Box without a React warning.
const DialogLeadForm = forwardRef<HTMLDivElement, DialogLeadFormProps>(({ open, value, defaults, onSave, onClose }, ref) => {
  const dispatch = useAppDispatch();

  const [sources, setSources] = useState<any[]>([]);
  const [subSources, setSubSources] = useState<any[]>([]);
  const [agents, setAgents] = useState<any[]>([]);

  const [source, setSource] = useState('');
  const [subSource, setSubSource] = useState('');
  const [agent, setAgent] = useState('');

  // Guards so resolving the CRM labels happens once per open and never fights a
  // user who then picks something else.
  const seededSource = useRef(false);
  const seededSubSource = useRef(false);
  const seededAgent = useRef(false);

  // Seed the form from the invoice's current selection each time it opens.
  useEffect(() => {
    if (!open) return;
    setSource(value?.sourceRef || '');
    setSubSource(value?.subSourceRef || '');
    setAgent(value?.agentId || '');
    seededSource.current = false;
    seededSubSource.current = false;
    seededAgent.current = false;
  }, [open, value]);

  // The imported lead carries labels, not our master ids, so match them by name.
  const findByLabel = (list: any[], label?: string) =>
    label ? list.find((o: any) => String(o.label).trim().toLowerCase() === String(label).trim().toLowerCase()) : undefined;

  useEffect(() => {
    if (!open || seededSource.current || value?.sourceRef || !sources.length) return;
    const match = findByLabel(sources, defaults?.sourceName);
    seededSource.current = true;
    if (match) setSource(match._id);
  }, [open, sources, value, defaults]);

  useEffect(() => {
    if (!open || seededSubSource.current || value?.subSourceRef || !subSources.length) return;
    const match = findByLabel(subSources, defaults?.subSourceName);
    seededSubSource.current = true;
    if (match) setSubSource(match._id);
  }, [open, subSources, value, defaults]);

  useEffect(() => {
    if (!open || seededAgent.current || value?.agentId || !agents.length) return;
    const name = defaults?.agentName;
    const match = name ? agents.find((a: any) => String(a.name).trim().toLowerCase() === String(name).trim().toLowerCase()) : undefined;
    seededAgent.current = true;
    if (match) setAgent(match.id);
  }, [open, agents, value, defaults]);

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      try {
        const sourceData: any = await dispatch(fetchLeadSources()).unwrap();
        setSources(sourceData?.data || []);
      } catch (error) {
        console.error('Error fetching lead sources:', error);
      }

      try {
        const agentData: any = await dispatch(fetchUnofyCrmAgents()).unwrap();
        setAgents(agentData?.data || []);
      } catch (error) {
        console.error('Error fetching CRM agents:', error);
      }
    };

    load();
  }, [open, dispatch]);

  // Sub-sources belong to a source, so they reload whenever the source changes.
  useEffect(() => {
    if (!open || !source) {
      setSubSources([]);
      return;
    }

    const loadSubSources = async () => {
      try {
        const data: any = await dispatch(fetchLeadSubSources(source)).unwrap();
        setSubSources(data?.data || []);
      } catch (error) {
        console.error('Error fetching lead sub-sources:', error);
        setSubSources([]);
      }
    };

    loadSubSources();
  }, [open, source, dispatch]);

  const onSourceChange = (event: SelectChangeEvent<any>) => {
    setSource(event.target.value);
    setSubSource(''); // the old sub-source belongs to the old source
  };

  const handleSave = () => {
    const selectedAgent = agents.find((a: any) => a.id === agent);
    const selectedSource = sources.find((s: any) => s._id === source);
    const selectedSubSource = subSources.find((s: any) => s._id === subSource);

    const sourceLabel = selectedSource ? selectedSource.label : '';
    const subSourceLabel = selectedSubSource ? selectedSubSource.label : '';

    onSave({
      sourceRef: source,
      sourceLabel,
      subSourceRef: subSource,
      subSourceLabel,
      // A source with no sub-sources (or none picked) is itself the attribution.
      sourceValue: subSourceLabel || sourceLabel,
      agentId: agent,
      agentName: selectedAgent ? selectedAgent.name : '',
    });
    onClose();
  };

  return (
    <Box sx={style} ref={ref}>
      <Grid container>
        <Grid {...{ xs: 12, sm: 8, md: 4, lg: 12, p: 2, pl: 2, bgcolor: '#e9ecf1' }} style={{ borderTopLeftRadius: 8, borderTopRightRadius: 8, height: 50 }}>
          <div style={{ float: 'right', marginTop: -7, marginRight: -5 }}>
            <Button
              style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
              variant="contained"
              size='small'
              onClick={handleSave}>
              Save
            </Button>
            <Button
              style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
              variant="contained"
              size='small'
              onClick={onClose}>
              Cancel
            </Button>
          </div>
          <h3 style={{ marginTop: -2 }}>Lead Details</h3>
        </Grid>
      </Grid>

      <Grid container p={2} spacing={2}>
        {defaults?.leadId &&
          <Grid item xs={12} pb={0}>
            <h5 style={labelStyle}>Lead ID</h5>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#488dff' }}>{defaults.leadId}</span>
          </Grid>}

        <Grid item xs={6} pr={1}>
          <h5 style={labelStyle}>Source</h5>
          <Select
            className='c-text-field-4'
            disableUnderline
            displayEmpty
            size="small"
            variant='outlined'
            MenuProps={menuProps}
            sx={selectSx}
            value={source}
            onChange={onSourceChange}
          >
            <MenuItem value='' key='none'>Select Source</MenuItem>
            {sources.map((option: any) => (
              <MenuItem value={option._id} key={option._id}>{option.label}</MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={6} pl={1}>
          <h5 style={labelStyle}>Sub Source</h5>
          <Select
            className='c-text-field-4'
            disableUnderline
            displayEmpty
            size="small"
            variant='outlined'
            disabled={!source}
            MenuProps={menuProps}
            sx={selectSx}
            value={subSource}
            onChange={(event: SelectChangeEvent<any>) => setSubSource(event.target.value)}
          >
            <MenuItem value='' key='none'>
              {!source ? 'Select a source first' : (subSources.length ? 'Select Sub Source' : 'No sub sources')}
            </MenuItem>
            {subSources.map((option: any) => (
              <MenuItem value={option._id} key={option._id}>{option.label}</MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={12}>
          <h5 style={labelStyle}>Agent</h5>
          <Select
            className='c-text-field-4'
            disableUnderline
            displayEmpty
            size="small"
            variant='outlined'
            MenuProps={menuProps}
            sx={selectSx}
            value={agent}
            onChange={(event: SelectChangeEvent<any>) => setAgent(event.target.value)}
          >
            <MenuItem value='' key='none'>Select Agent</MenuItem>
            {agents.map((option: any) => (
              <MenuItem value={option.id} key={option.id}>
                {option.name}{option.role ? ` — ${option.role}` : ''}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
    </Box>
  );
});

export default DialogLeadForm;
